import * as THREE from 'three/webgpu';
import { integrateTrajectory } from '../../engine/gravity/GravityField';
import { type DynamicBody, type PhysicsWorld, RAPIER } from '../../engine/physics/PhysicsWorld';
import type { RenderOrigin } from '../../engine/render/RenderOrigin';

const MAX_STONES = 48;
const STONE_RADIUS = 0.14;
const THROW_SPEED = 26;
const PREVIEW_POINTS = 420;

const _p = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3(1, 1, 1);

/**
 * Throwable stones: dynamic Rapier balls that fall in the true gravity field.
 * Holding the right mouse button previews the arc (integrated in f64).
 */
export class Stones {
  private readonly stones: DynamicBody[] = [];
  readonly mesh: THREE.InstancedMesh;
  readonly arc: THREE.Line;
  private readonly arcPositions: Float32Array;
  private readonly arcUniverse = new Float64Array(PREVIEW_POINTS * 3);
  private arcCount = 0;

  constructor(
    private readonly physics: PhysicsWorld,
    private readonly origin: RenderOrigin,
    scene: THREE.Scene,
  ) {
    const mat = new THREE.MeshStandardNodeMaterial({ color: 0x9b958a, roughness: 0.8 });
    this.mesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(STONE_RADIUS, 2), mat, MAX_STONES);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);

    this.arcPositions = new Float32Array(PREVIEW_POINTS * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.arcPositions, 3));
    this.arc = new THREE.Line(geo, new THREE.LineBasicNodeMaterial({ color: 0xffd27a }));
    this.arc.frustumCulled = false;
    this.arc.visible = false;
    scene.add(this.arc);
  }

  throw(from: THREE.Vector3, dir: THREE.Vector3, inherit: THREE.Vector3): void {
    if (this.stones.length >= MAX_STONES) {
      const old = this.stones.shift()!;
      this.physics.removeDynamic(old);
    }
    this.physics.toLocal(from, _p);
    const v = dir.clone().multiplyScalar(THROW_SPEED).add(inherit);
    const body = this.physics.world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(_p.x, _p.y, _p.z)
        .setLinvel(v.x, v.y, v.z)
        .setCcdEnabled(true),
    );
    this.physics.world.createCollider(
      RAPIER.ColliderDesc.ball(STONE_RADIUS).setDensity(2600).setRestitution(0.35).setFriction(0.7),
      body,
    );
    this.stones.push(this.physics.addDynamic(body));
  }

  /** Recompute the preview arc from a throw origin/direction. */
  preview(
    field: Parameters<typeof integrateTrajectory>[0],
    from: THREE.Vector3,
    dir: THREE.Vector3,
    inherit: THREE.Vector3,
    stop: (p: THREE.Vector3) => boolean,
  ): void {
    const v = dir.clone().multiplyScalar(THROW_SPEED).add(inherit);
    this.arcCount = integrateTrajectory(field, from, v, 1 / 60, PREVIEW_POINTS, this.arcUniverse, stop);
  }

  setPreviewVisible(v: boolean): void {
    this.arc.visible = v;
  }

  /** Per-frame: place instances and the arc camera-relative. */
  frame(): void {
    const n = this.stones.length;
    for (let i = 0; i < n; i++) {
      const b = this.stones[i].body;
      this.physics.toUniverse(b.translation(), _p);
      this.origin.toRender(_p, _p);
      const r = b.rotation();
      _q.set(r.x, r.y, r.z, r.w);
      _m.compose(_p, _q, _s);
      this.mesh.setMatrixAt(i, _m);
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;

    if (this.arc.visible) {
      const cam = this.origin.camera;
      for (let i = 0; i < this.arcCount; i++) {
        this.arcPositions[i * 3] = this.arcUniverse[i * 3] - cam.x;
        this.arcPositions[i * 3 + 1] = this.arcUniverse[i * 3 + 1] - cam.y;
        this.arcPositions[i * 3 + 2] = this.arcUniverse[i * 3 + 2] - cam.z;
      }
      const attr = this.arc.geometry.getAttribute('position') as THREE.BufferAttribute;
      attr.needsUpdate = true;
      this.arc.geometry.setDrawRange(0, this.arcCount);
    }
  }
}
